import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_LONG_MS } from "@/lib/query-constants";

const OPPORTUNITIES_PATH = "/post-flight/opportunities";

export type PostFlightScoreResult = "weak" | "ok";

export interface PostFlightOpportunity {
  opportunity_type: string;
  window_days: number;
  window_start?: string | null;
  window_end?: string | null;
  source: string;
  status: string;
  hash: string;
  rank?: number;
  primary_concept?: string | null;
  days_live?: number | null;
  spend_share?: number | null;
  kpi?: string | null;
  pct_better?: number | null;
  kpi_value?: number | null;
  baseline_value?: number | null;
  purchases?: number | null;
  cpa?: number | null;
  account_cpa?: number | null;
  cpa_first_half?: number | null;
  cpa_second_half?: number | null;
  persona?: string | null;
  motivation?: string | null;
  pain_point?: string | null;
  first_seen?: string | null;
  evidence?: string | null;
  reason?: string | null;
  video_url?: string | null;
  object_type?: string | null;
  spend?: number | null;
  thumbstop?: number | null;
  thumbstop_expected?: number | null;
  hold_rate?: number | null;
  account_hold_rate?: number | null;
  active?: boolean | number | null;
  lever_1?: string | null;
  lever_2?: string | null;
  lever_3?: string | null;
  hook_score?: number | null;
  hold_score?: number | null;
  hook_result?: PostFlightScoreResult | null;
  hold_result?: PostFlightScoreResult | null;
}

export interface PostFlightSwitchTo {
  section: string;
  window_days?: number | null;
  source?: string | null;
  rank?: number | null;
  item?: string | null;
  flag?: string | null;
  concept?: string | null;
  product?: string | null;
  best_line?: string | null;
  note?: string | null;
  hook_score?: number | null;
  hold_score?: number | null;
}

export interface PostFlightList {
  client: string;
  environment: string;
  dataset: string;
  as_of: string | null;
  opportunities: PostFlightOpportunity[];
  cutoffs: Array<Record<string, unknown>>;
  switch_to?: PostFlightSwitchTo[];
  warnings: string[];
  fetchedAt?: string;
  stale?: boolean;
}

/** One list for every window. The page filters it. The timeout stays within the shared API edge. */
export async function listPostFlightOpportunities(): Promise<PostFlightList> {
  const { data } = await authFetch<PostFlightList>(OPPORTUNITIES_PATH, {
    timeoutMs: API_REQUEST_TIMEOUT_LONG_MS,
  });
  return data;
}

export interface PostFlightDoubleDownBlock {
  leverId: string | null;
  instruction: string;
  title: string;
  scriptCount: number;
}

export interface StartPostFlightDoubleDownRequest {
  tenantId: string;
  idempotencyKey: string;
  hash: string;
  windowDays: number;
  source: string;
  blocks: PostFlightDoubleDownBlock[];
}

const DOUBLE_DOWN_PATH = "/post-flight/double-down";

/** Starts one Amber job that writes the chosen blocks into a new Post-Flight set. */
export async function startPostFlightDoubleDown(
  body: StartPostFlightDoubleDownRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(DOUBLE_DOWN_PATH, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!correlationId) {
    throw new Error("Double down response missing correlationId");
  }
  return { correlationId };
}
